// Upload large files directly to Google Cloud Storage using a signed URL.
// Bypasses the Cloud Run 32 MB HTTP/1 request body limit.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Get a signed upload URL from the backend, then PUT the file directly to GCS.
 *
 * Uses native fetch() for the cross-origin PUT (axios has been observed to throw
 * "Network Error" on successful but slow CORS PUTs to GCS).
 *
 * @param {File} file - the browser File to upload
 * @param {string} folder - one of: reviews, queries, correspondence, documents, uploads
 * @param {(percent:number)=>void} onProgress - optional progress callback (0-100)
 * @returns {Promise<{gcs_path:string, file_size:number, content_type:string, original_filename:string}>}
 */
export async function uploadFileToGCS(file, folder = 'uploads', onProgress = null) {
  if (!file) throw new Error('No file provided');

  const contentType = file.type || 'application/octet-stream';

  // 1) Ask backend for a signed URL (must use the same content_type for signing)
  const signedRes = await axios.post(`${API}/uploads/signed-url`, {
    filename: file.name,
    content_type: contentType,
    folder,
  });
  const { upload_url, gcs_path } = signedRes.data;
  if (!upload_url || !gcs_path) {
    throw new Error('Failed to obtain signed upload URL');
  }

  // 2) PUT the file directly to GCS using fetch (no auth interceptors, full Blob streaming).
  // XHR is used for upload progress reporting; fetch doesn't expose request-side progress.
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', upload_url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed (HTTP ${xhr.status}): ${xhr.responseText?.slice(0, 200) || ''}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during GCS upload (check browser CORS / connectivity)'));
    xhr.ontimeout = () => reject(new Error('GCS upload timed out'));
    xhr.timeout = 30 * 60 * 1000;  // 30 min
    xhr.send(file);
  });

  return {
    gcs_path,
    file_size: file.size,
    content_type: contentType,
    original_filename: file.name,
  };
}
